import { KeyboardOutlined } from "@mui/icons-material";
import { Box, Button, Divider, Stack, useMediaQuery } from "@mui/material";
import React from "react";

import { NO_LABEL, YES_LABEL } from "config/LabelMapping";
import { useBoundingBoxesContext } from "context/boundingBoxesContext";
import { StepNavigation } from "features/canvas/components/StepsNavigator";
import { useReviewPageHotkeys } from "hooks/useHotkeys/useHotkeys";
import { CountButton } from "new-components/buttons/CountButton";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { Posicheck } from "types/imageQueries";

import { UnclearActionsRow } from "./UnclearActionsRow";

export interface BinaryImageActionsProps {
  imageQueryId?: Posicheck["id"];
  isUnclearMenuOpen: boolean;
  setUnclearMenuOpen: (isOpen: boolean) => void;
  onUnclearSubmit: (feedbackText: string) => void;
  onLabelSubmit: (label: string) => void;
  showDeleteButton?: boolean;
  onDeleteImageQuery?: () => void | Promise<void>;
  isDeletingImageQuery?: boolean;
}

export function BinaryImageActions(props: BinaryImageActionsProps) {
  const {
    imageQueryId,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    onUnclearSubmit,
    onLabelSubmit,
    showDeleteButton,
    onDeleteImageQuery,
    isDeletingImageQuery,
  } = props;

  const hasImageQuery = Boolean(imageQueryId);

  const {
    validBoxLabels,
    annotations,
    stepsData: { currentStep },
    handleChangeSteps,
  } = useBoundingBoxesContext();

  const { useUnclearHotkey, useYesHotkey, useNoHotkey } = useReviewPageHotkeys({
    detectorMode: DetectorModes.BINARY,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    hasImageQuery,
    onLabelSubmit,
  });

  useYesHotkey();
  useNoHotkey();
  useUnclearHotkey();

  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <>
      {validBoxLabels.length > 0 && <StepNavigation />}
      <Stack
        id="binary-actions"
        direction={{ xs: "column", sm: "row" }}
        className="bg-[#E5E9ED] rounded mx-[43px] my-[33px] items-center"
      >
        {validBoxLabels.length > 0 && currentStep <= validBoxLabels.length ? (
          <Box id="count-actions-options" height="100%" flexGrow={1}>
            <CountButton
              maxCount={25}
              handleCountSubmit={() => handleChangeSteps(currentStep + 1)}
              value={annotations.length}
              backgroundColor={validBoxLabels[currentStep - 1]?.color ?? "#529FE8"}
            />
          </Box>
        ) : (
          <Box id="binary-actions-options" height="100%" width="100%" flexGrow={1}>
            <Box className="flex gap-2" height="100%" width="100%">
              <Button
                id="binary-actions-yes-button"
                onClick={() => onLabelSubmit(YES_LABEL)}
                variant="contained"
                sx={{
                  width: "100%",
                  height: "34px",
                  bgcolor: "#6151da",
                  color: "#FFFFFF",
                  justifyContent: "center",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  borderRadius: "17px",
                  "& .MuiButton-endIcon": {
                    color: "FFFFFF",
                  },
                  "&:hover": {
                    bgcolor: "#6151da",
                    opacity: 0.8,
                  },
                }}
                endIcon={
                  <Box className="items-center opacity-60 h-full hidden md:flex gap-1">
                    <KeyboardOutlined sx={{ height: "24px", width: "24px" }} />
                    <Typography variant="small" fontWeight="bold">
                      1
                    </Typography>
                  </Box>
                }
              >
                <Typography noWrap>Yes</Typography>
              </Button>
              <Button
                id="binary-actions-no-button"
                onClick={() => onLabelSubmit(NO_LABEL)}
                variant="contained"
                sx={{
                  width: "100%",
                  height: "34px",
                  bgcolor: "#a02cdf",
                  color: "#FFFFFF",
                  justifyContent: "center",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  borderRadius: "17px",
                  "& .MuiButton-endIcon": {
                    color: "FFFFFF",
                  },
                  "&:hover": {
                    bgcolor: "#a02cdf",
                    opacity: 0.8,
                  },
                }}
                endIcon={
                  <Box className="items-center opacity-60 h-full hidden md:flex gap-1">
                    <KeyboardOutlined sx={{ height: "24px", width: "24px" }} />
                    <Typography variant="small" fontWeight="bold">
                      2
                    </Typography>
                  </Box>
                }
              >
                <Typography noWrap>No</Typography>
              </Button>
            </Box>
          </Box>
        )}
        <Divider
          id="binary-actions-divider"
          flexItem
          orientation={isMobile ? "horizontal" : "vertical"}
          sx={{
            marginX: { xs: "0px", sm: "20px" },
            height: { xs: "1px", sm: "34px" },
            marginY: { xs: "10px", sm: "0px" },
            borderColor: "#000000",
          }}
        />
        <Box flexShrink={0}>
          <UnclearActionsRow
            hasImageQuery={hasImageQuery}
            isUnclearMenuOpen={isUnclearMenuOpen}
            setUnclearMenuOpen={setUnclearMenuOpen}
            onUnclearSubmit={onUnclearSubmit}
            showDeleteButton={showDeleteButton}
            onDeleteImageQuery={onDeleteImageQuery}
            isDeletingImageQuery={isDeletingImageQuery}
          />
        </Box>
      </Stack>
    </>
  );
}
